/**
 * FamilyMemberAvatar Component
 *
 * Reusable avatar displaying a family member's initial on a colored background.
 * Supports multiple sizes for different contexts.
 */

import { Image, type ImageStyle, StyleSheet, Text, View, ViewStyle } from 'react-native';

type AvatarSize = 'small' | 'medium' | 'large';

type FamilyMemberAvatarProps = {
  name: string;
  color: string;
  size?: AvatarSize;
  style?: ViewStyle;
  imageUri?: string | null;
};

const SIZES = {
  small: {
    container: 40,
    fontSize: 16,
  },
  medium: {
    container: 60,
    fontSize: 24,
  },
  large: {
    container: 100,
    fontSize: 40,
  },
} as const;

export function FamilyMemberAvatar({
  name,
  color,
  size = 'medium',
  style,
  imageUri,
}: FamilyMemberAvatarProps) {
  const initial = name.charAt(0).toUpperCase();
  const sizeConfig = SIZES[size];

  // Show profile picture if available
  if (imageUri) {
    return (
      <Image
        source={{ uri: imageUri }}
        style={[
          styles.image,
          {
            width: sizeConfig.container,
            height: sizeConfig.container,
            borderRadius: sizeConfig.container / 2,
          },
          style as ImageStyle | undefined,
        ]}
      />
    );
  }

  // Fall back to initial
  return (
    <View
      style={[
        styles.container,
        {
          width: sizeConfig.container,
          height: sizeConfig.container,
          borderRadius: sizeConfig.container / 2,
          backgroundColor: color,
        },
        style,
      ]}
    >
      <Text style={[styles.initial, { fontSize: sizeConfig.fontSize }]}>
        {initial}
      </Text>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  initial: {
    fontWeight: '600',
    color: '#FFFFFF',
  },
  image: {
    resizeMode: 'cover',
  },
});
